import { useEffect, useState } from 'react';
import { getGuid } from '../Utils/stringUtils';
import { useGlobalState } from '../Contexts/globalStateContext';
import DirtyDialog from '../Components/Popup/DirtyDialog';
import { useTranslation } from 'react-i18next';

export type Validator<T> = (value: T) => boolean;
export type FormStateChangeHandler = (state: { name: string; isValid?: boolean; isTouched?: boolean }) => void;

export const requiredValidator: Validator<string | undefined | null | unknown[]> = (value) => {
	if (!value) {
		return false;
	} else if (typeof value === 'string') {
		return value.trim() !== '';
	} else if (Array.isArray(value)) {
		return value.length > 0;
	}
	return false;
};

export const positiveNumberValidator: Validator<string | undefined | null> = (value) => {
	if (!value) return false;
	const number = Number(value);
	return !isNaN(number) && number > 0;
};

export const emailValidator: Validator<string | undefined | null> = (value) => {
	if (!value) return true;
	const emailRegex = /^\w+([-+.']\w+)*@\w+([-.]\w+)*\.\w+([-.]\w+)*$/;
	return emailRegex.test(value);
};

export interface FormFieldConfig<T = string | undefined | null> {
	validators?: Validator<T>[];
	state: FormState;
	group?: string;
}

function validateFieldValue<T>(value: T, validators: Validator<T>[]) {
	let isValid = true;
	for (const val of validators) {
		if (!val(value)) {
			isValid = false;
			break;
		}
	}
	return isValid;
}

export interface FormState {
	formStateId: string;
	fields: {
		[key: string]: {
			isValid: boolean;
			group?: string;
		};
	};
	isFormValid: boolean;
	isDirty: boolean;
	showValidation: boolean;
	setFieldValid: (name: string, isValid: boolean, group?: string) => void;
	setDirty: () => void;
	setPristine: () => void;
	setShowValidation: (show: boolean) => void;
	validateFieldValue: <T>(value: T, validators: Validator<T>[]) => boolean;
	removeField: (name: string) => void;
}

export const useForm = (config: { id?: string; customSetDirtyFn?: (isDirty: boolean) => void }) => {
	const [formState, setFormState] = useState<FormState>({
		formStateId: getGuid(),
		fields: {},
		isFormValid: true,
		isDirty: false,
		showValidation: false,
		setFieldValid,
		setDirty,
		setPristine,
		setShowValidation,
		validateFieldValue,
		removeField,
	});

	useEffect(() => {
		clearFormState();
	}, [config.id]);

	function clearFormState() {
		//console.log('clearing form state');
		if (config.customSetDirtyFn) {
			config.customSetDirtyFn(false);
		}
		setFormState((current) => {
			//console.log("resetting formState")
			return {
				...current,
				formStateId: getGuid(),
				fields: {},
				isFormValid: true,
				isDirty: false,
				showValidation: false,
			};
		});
	}

	function isGroupValid(group: string) {
		return Object.values(formState.fields)
			.filter((f) => f.group === group)
			.every((f) => f.isValid);
	}

	function setFieldValid(name: string, isValid: boolean, group?: string) {
		setFormState((current) => {
			//console.log(`setting field ${name} to ${isValid} in group ${group}`);
			const newFields = {
				...current.fields,
				[name]: { isValid, group },
			};
			const isFormValid = Object.values(newFields).every((f) => f.isValid);
			return {
				...current,
				fields: newFields,
				isFormValid,
			};
		});
	}

	function removeField(name: string) {
		setFormState((current) => {
			const newFields = { ...current.fields };
			delete newFields[name];
			const isFormValid = Object.values(newFields).every((f) => f.isValid);
			return {
				...current,
				fields: newFields,
				isFormValid,
			};
		});
	}

	function setDirty() {
		if (!formState.isDirty) {
			if (config.customSetDirtyFn) {
				config.customSetDirtyFn(true);
			}
			setFormState((current) => {
				return { ...current, isDirty: true };
			});
		}
	}

	function setPristine() {
		if (!formState.isDirty) {
			if (config.customSetDirtyFn) {
				config.customSetDirtyFn(false);
			}
			setFormState((current) => {
				return { ...current, isDirty: false };
			});
		}
	}

	function setShowValidation(show: boolean) {
		setFormState((current) => {
			return { ...current, showValidation: show };
		});
	}

	return {
		formState,
		isGroupValid,
		clearFormState,
	};
};

export const useHandlePossibleDirtyPage = () => {
	const { t } = useTranslation();
	const {
		showModal,
		setDirty,
		state: { pageDirty },
	} = useGlobalState();

	const handlePossibleDirtyPage = (onConfirm: () => void) => {
		if (!pageDirty) {
			onConfirm();
			return;
		}

		showModal(
			<DirtyDialog
				haltedAction={() => {
					setDirty(false);
					onConfirm();
				}}
			/>,
			t('DIRTY_DIALOG.TITLE', { defaultValue: 'Unsaved changes' })
		);
	};

	return handlePossibleDirtyPage;
};
